import type { Decor } from '../world/mapdef';
import { block, ellipse, hash, rect, shade, shadow, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers, type ObjDrawer } from './propRegistry';
import { LIFE_ANIMATED, drawLife, person } from './streetLife';
import { isNight, line, poly, stall, steam, txt, vgrad } from './propsHualien';

/**
 * Tainan (tainan package): the HSR station, Chihkan Tower's two pavilions and its stone turtles, the
 * Confucius Temple's great hall and the 全臺首學 gate, Shuixian and Matsu temples, Fort Zeelandia's white
 * watchtower and brick ramparts, the Anping Tree House, four kinds of shophouse row, canal boats and
 * quays, lantern workshops, cracker racks, candied-fruit jars, old banyans, and the drawers of every
 * interactive object (food stalls, info boards, the toilet, the rental kiosk and the ATM).
 *
 * Big pictures are rendered once per look (day/night) into bitmaps and blitted (`blit`); landmarks are
 * drawn in 80 px slices (`landmark`) so they never pop out of view. Street life (`tn_crowd`, `tn_person`,
 * `tn_vsign`…) is delegated to streetLife.ts.
 */

const RED = '#b8382c', RED_L = '#d9503c', RED_D = '#7a2418';
const GOLD = '#e8b84a';
const BRICK = '#a8452f', BRICK_L = '#c25d3f';
const TILE_GREY = '#5d6a6a', TILE_ORANGE = '#c8673a';
const WOOD = '#6a4a2e', WOOD_L = '#8a6a44';

// ---------------------------------------------------------------------------
// Bitmap cache
// ---------------------------------------------------------------------------

const ART = new Map<string, HTMLCanvasElement>();

/** Blit a cached picture whose origin is its bottom-centre at (x, y); `y0..y1` select a vertical slice. */
function blit(c: Ctx, key: string, x: number, y: number, w: number, h: number, paint: (c: Ctx) => void, y0 = -h, y1 = 8): void {
  const s = Math.min(3, Math.max(1, Math.ceil(Math.abs(c.getTransform().a) - 0.05)));
  const k = `${key}@${s}`;
  let cv = ART.get(k);
  if (!cv) {
    cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil((h + 8) * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(w / 2, h);
    paint(cc);
    ART.set(k, cv);
  }
  const sy = (y0 + h) * s, sh = (y1 - y0) * s;
  if (sh <= 0) return;
  c.drawImage(cv, 0, sy, cv.width, Math.min(sh, cv.height - sy), x - w / 2, y + y0, w, Math.min(y1 - y0, cv.height / s - (y0 + h)));
}

const SLICE = 80;
/** Draw slice `d.variant % 8` of a landmark (`big()` in the map); `d.h` is the full height, `d.w` the width. */
function landmark(c: Ctx, d: Decor, key: string, paint: (c: Ctx, night: boolean) => void): void {
  const k = (d.variant ?? 0) % 8, h = d.h ?? 90, w = d.w ?? 64;
  const n = Math.max(1, Math.ceil((h - 90) / SLICE) + 1);
  const bottom = d.y + SLICE * k;
  const y1 = k === 0 ? 8 : -SLICE * k;
  const y0 = k === n - 1 ? -h : -SLICE * (k + 1);
  const night = isNight();
  blit(c, `${key}${night ? 'N' : 'D'}`, d.x, bottom, w, h, (cc) => paint(cc, night), y0, y1);
}

/** A cached, unsliced picture anchored at the decor's bottom-centre. */
function art(c: Ctx, d: Decor, key: string, w: number, h: number, paint: (c: Ctx, night: boolean) => void, nightAware = true): void {
  const night = nightAware && isNight();
  blit(c, `${key}:${d.variant ?? 0}:${d.w ?? 0}${night ? 'N' : 'D'}`, d.x, d.y, w, h, (cc) => paint(cc, night));
}

const rnd = (seed: number, k: number) => hash(seed, k, 733);

// ---------------------------------------------------------------------------
// Shared architectural bits
// ---------------------------------------------------------------------------

/**
 * Southern-Fujian roof with swallow-tail (燕尾) eaves curling up at both ends: from `l` to `r`, eave line
 * at `y`, ridge `h` above. Tile courses, a pale ridge band and, if `dragons`, little ridge beasts.
 */
function swallowRoof(c: Ctx, l: number, r: number, y: number, h: number, tile: string, ridge: string, dragons = false): void {
  const w = r - l, cx = (l + r) / 2;
  const curl = Math.min(10, w * 0.08);
  // Underside shadow.
  c.fillStyle = shade(tile, -0.4);
  c.beginPath();
  c.moveTo(l - 6, y - curl - 3);
  c.quadraticCurveTo(l + w * 0.1, y + 2, l + w * 0.28, y + 1.5);
  c.lineTo(r - w * 0.28, y + 1.5);
  c.quadraticCurveTo(r - w * 0.1, y + 2, r + 6, y - curl - 3);
  c.lineTo(r - w * 0.14, y - h);
  c.lineTo(l + w * 0.14, y - h);
  c.closePath();
  c.fill();
  c.fillStyle = tile;
  c.beginPath();
  c.moveTo(l - 4, y - curl - 2);
  c.quadraticCurveTo(l + w * 0.1, y, l + w * 0.28, y - 0.4);
  c.lineTo(r - w * 0.28, y - 0.4);
  c.quadraticCurveTo(r - w * 0.1, y, r + 4, y - curl - 2);
  c.lineTo(r - w * 0.15, y - h + 1);
  c.lineTo(l + w * 0.15, y - h + 1);
  c.closePath();
  c.fill();
  // Tile courses fanning slightly.
  for (let i = l + 3; i < r - 2; i += 2.6) {
    const k = Math.abs(i - cx) / (w / 2);
    rect(c, 'rgba(40,20,10,0.26)', i, y - h + 2 + k * 2, 0.6, h - 4 - k * 3);
  }
  rect(c, 'rgba(255,255,255,0.18)', l + w * 0.16, y - h + 1.6, w * 0.68, 0.7);
  // Ridge with upturned ends.
  block(c, l + w * 0.13, y - h - 2.6, w * 0.74, 3.2, ridge, shade(ridge, 0.3), shade(ridge, -0.3));
  for (const s of [-1, 1]) {
    const ex = s < 0 ? l + w * 0.13 : r - w * 0.13;
    poly(c, ridge, [ex, y - h - 2.6, ex + s * 4.5, y - h - 7, ex + s * 1.8, y - h + 0.6]);
  }
  if (dragons) {
    for (const s of [-1, 1]) {
      const dx = cx + s * w * 0.26;
      poly(c, '#3fa36f', [dx - 3, y - h - 2.6, dx - 1, y - h - 7, dx + 1.5, y - h - 5.5, dx + 3, y - h - 8.5, dx + 3.5, y - h - 2.6]);
      ellipse(c, '#ffd34e', dx + 2.6, y - h - 7.5, 0.8, 0.8);
    }
    // Pearl in the middle of the ridge.
    ellipse(c, '#ff6a3a', cx, y - h - 5, 2, 2);
    ellipse(c, '#ffe0a0', cx - 0.6, y - h - 5.6, 0.7, 0.7);
  }
}

/** A red lacquered column with a stone base. */
function column(c: Ctx, x: number, top: number, bottom: number, w = 3.2, col = RED): void {
  block(c, x - w / 2, top, w, bottom - top, col, shade(col, 0.25), shade(col, -0.3));
  block(c, x - w / 2 - 0.8, bottom - 3, w + 1.6, 3, '#9a948a', '#b8b4ac', '#6a6660');
}

/** A hanging red lantern (glows at night). */
function redLantern(c: Ctx, x: number, y: number, night: boolean, r = 3.4): void {
  rect(c, '#2a1810', x - 0.4, y - 3, 0.8, 3);
  block(c, x - 2.2, y - 0.6, 4.4, 1.4, '#2a1810');
  ellipse(c, night ? '#ff6a4a' : '#d9412e', x, y + r, r, r * 0.9);
  for (let k = -1; k <= 1; k++) rect(c, 'rgba(60,10,0,0.3)', x + k * r * 0.5 - 0.3, y + 0.6, 0.6, r * 1.7);
  ellipse(c, 'rgba(255,240,180,0.6)', x - r * 0.3, y + r * 0.7, r * 0.3, r * 0.45);
  block(c, x - 1.6, y + r * 1.85, 3.2, 1.2, '#2a1810');
  rect(c, GOLD, x - 0.3, y + r * 1.85 + 1.2, 0.6, 3);
  if (night) ellipse(c, 'rgba(255,120,70,0.28)', x, y + r, r * 2.4, r * 2.2);
}

/** Door pair with a dark interior and door gods / studs. */
function doors(c: Ctx, x: number, y: number, w: number, h: number, night: boolean, col = RED): void {
  rect(c, night ? '#3a2014' : '#241410', x, y, w, h);
  for (const s of [0, 1]) {
    const dx = x + s * (w / 2) + 0.6, dw = w / 2 - 1.2;
    block(c, dx, y, dw, h, col, shade(col, 0.2), shade(col, -0.35));
    for (let j = 0; j < 3; j++) for (let i = 0; i < 2; i++) ellipse(c, GOLD, dx + 1.8 + i * (dw - 3.6), y + 2.5 + j * (h / 3), 0.7, 0.7);
    ellipse(c, GOLD, dx + dw / 2, y + h * 0.55, 1.1, 1.1);
  }
  if (night) rect(c, 'rgba(255,200,120,0.35)', x + w / 2 - 0.5, y + 2, 1, h - 4);
}

/** Plaque with gold characters on a dark board. */
function plaque(c: Ctx, text: string, cx: number, y: number, w: number, h = 8, bg = '#1f2a3a'): void {
  block(c, cx - w / 2, y, w, h, bg, shade(bg, 0.35), shade(bg, -0.4));
  rect(c, GOLD, cx - w / 2 + 0.8, y + 0.8, w - 1.6, 0.5);
  rect(c, GOLD, cx - w / 2 + 0.8, y + h - 1.3, w - 1.6, 0.5);
  txt(c, text, cx, y + h / 2 + 0.2, h * 0.68, GOLD, 900, 'rgba(0,0,0,0.4)');
}

/** A brick wall patch with mortar lines. */
function brickwork(c: Ctx, x: number, y: number, w: number, h: number, seed: number, a = BRICK, b = BRICK_L): void {
  rect(c, a, x, y, w, h);
  for (let j = 0; j < h; j += 3) {
    const off = (Math.floor(j / 3) % 2) * 3;
    for (let i = -off; i < w; i += 6) {
      const bx = x + Math.max(0, i), bw = Math.min(6, w - Math.max(0, i), i < 0 ? 6 + i : 6);
      if (bw <= 0) continue;
      const t = rnd(seed, i * 17 + j * 3);
      rect(c, t < 0.2 ? shade(a, -0.15) : t > 0.8 ? b : a, bx, y + j, bw - 0.6, 2.4);
    }
  }
  rect(c, 'rgba(255,255,255,0.08)', x, y, w, 0.6);
}

/** Glass window pane: sky by day, warm (sometimes dark) at night. */
function pane(c: Ctx, x: number, y: number, w: number, h: number, night: boolean, seed: number, frame = '#e8e4da'): void {
  rect(c, shade(frame, -0.35), x - 0.5, y - 0.5, w + 1, h + 1);
  if (night) {
    const lit = rnd(seed, 7) < 0.65;
    rect(c, lit ? (rnd(seed, 9) < 0.3 ? '#ffe9b0' : '#ffd88a') : '#2a3448', x, y, w, h);
    if (lit) rect(c, 'rgba(255,255,255,0.35)', x, y, w, 0.8);
  } else {
    vgrad(c, x, y, w, h, '#a8cfe4', '#5d86a4');
    rect(c, 'rgba(255,255,255,0.45)', x + 0.6, y + 0.6, Math.max(0.6, w * 0.25), h - 1.2);
  }
}

/** Wooden shutter window (old brick houses). */
function shutter(c: Ctx, x: number, y: number, w: number, h: number, night: boolean, col = WOOD): void {
  rect(c, '#2a1810', x - 0.6, y - 0.6, w + 1.2, h + 1.2);
  rect(c, night ? '#ffd88a' : '#1d1a22', x, y, w, h);
  block(c, x, y, w / 2 - 0.4, h, col, shade(col, 0.2), shade(col, -0.3));
  for (let j = 1; j < h - 1; j += 2.4) rect(c, 'rgba(0,0,0,0.2)', x + 0.6, y + j, w / 2 - 1.6, 0.6);
  rect(c, night ? '#ffd88a' : '#2a3448', x + w / 2, y + 1, w / 2 - 1, h - 2);
  rect(c, '#8a7a5a', x + w / 2 + (w / 4) - 0.3, y + 1, 0.6, h - 2);
}

// ---------------------------------------------------------------------------
// HSR Tainan station
// ---------------------------------------------------------------------------

function hsrStation(c: Ctx, night: boolean): void {
  shadow(c, 0, 0, 90, 4, 0.3);
  // Elevated platform and viaduct behind the hall, with an HSR train standing at the platform.
  block(c, -92, -104, 184, 10, '#b8bcc2', '#d6d9dd', '#8a8f96');
  for (let i = -84; i < 90; i += 24) block(c, i, -94, 5, 26, '#a4a9b0', '#c3c7cc', '#7a7f86');
  // The train: white 700T with an orange stripe, a long nose on the right.
  const ty = -118;
  block(c, -88, ty, 160, 14, '#f4f4f0', '#ffffff', '#c8c8c0');
  rect(c, '#ff8a2a', -88, ty + 9, 160, 2.4);
  rect(c, '#2a2f3d', -88, ty + 12, 160, 1.6);
  for (let i = -82; i < 66; i += 9) rect(c, night ? '#ffe6a8' : '#3a4a66', i, ty + 3, 5, 4);
  poly(c, '#f4f4f0', [72, ty, 72, ty + 14, 96, ty + 14, 90, ty + 8]);
  poly(c, '#2a3448', [72, ty + 3, 72, ty + 8, 86, ty + 8, 80, ty + 4]);
  rect(c, '#ff8a2a', 72, ty + 9, 24, 2.4);
  // Platform canopy posts and roof.
  block(c, -92, -124, 184, 3, '#8a8f96', '#aab0b6', '#5a6066');
  // Main hall: a pale concrete box with a sweeping arched metal roof.
  block(c, -84, -70, 168, 70, '#e4e2dc', '#f4f2ee', '#b8b5ae');
  c.fillStyle = '#8f98a3';
  c.beginPath();
  c.moveTo(-90, -70);
  c.quadraticCurveTo(0, -112, 90, -70);
  c.lineTo(90, -66);
  c.quadraticCurveTo(0, -106, -90, -66);
  c.closePath();
  c.fill();
  c.fillStyle = '#c7ced6';
  c.beginPath();
  c.moveTo(-88, -70);
  c.quadraticCurveTo(0, -108, 88, -70);
  c.lineTo(88, -68.5);
  c.quadraticCurveTo(0, -106, -88, -68.5);
  c.closePath();
  c.fill();
  for (let i = -80; i <= 80; i += 16) {
    const k = i / 90;
    line(c, 'rgba(60,70,80,0.5)', 0.7, [i, -70, i, -70 - (1 - k * k) * 36 + 2]);
  }
  // Glass front with mullions and a tall central entrance.
  pane(c, -78, -60, 156, 48, night, 1, '#8a9096');
  for (let i = -78; i <= 78; i += 12) rect(c, '#6a7078', i - 0.5, -60, 1, 48);
  rect(c, '#6a7078', -78, -38, 156, 1);
  block(c, -22, -56, 44, 44, night ? '#ffe2a8' : '#bcdcee', night ? '#fff0c8' : '#d8ecf6', '#8a9096');
  rect(c, '#6a7078', -0.5, -56, 1, 44);
  rect(c, '#6a7078', -22, -34, 44, 1);
  // Sign band: 高鐵台南站 / Tainan Station.
  block(c, -52, -82, 104, 12, night ? '#2b3a55' : '#1f2a3a', '#334a6a', '#121a28');
  txt(c, '高鐵 台南站', -10, -76.2, 8.5, night ? '#ffffff' : '#f4f1ea', 900, 'rgba(0,0,0,0.4)');
  txt(c, 'THSR  Tainan', 30, -76.4, 3.6, '#ff8a2a', 800);
  // Clock.
  ellipse(c, '#f4f1ea', 60, -50, 5, 5);
  ellipse(c, '#2a2f3d', 60, -50, 4.2, 4.2);
  ellipse(c, '#f4f1ea', 60, -50, 3.6, 3.6);
  line(c, '#2a2f3d', 0.7, [60, -50, 60, -53]);
  line(c, '#2a2f3d', 0.7, [60, -50, 62.2, -49]);
  // Pillars and the canopy over the doors.
  for (const px of [-80, -40, 40, 80]) block(c, px - 2.5, -62, 5, 62, '#cfcbc3', '#e8e4dc', '#9a968e');
  block(c, -40, -22, 80, 4, '#b8bcc2', '#d6d9dd', '#8a8f96');
  for (let i = -36; i < 40; i += 8) rect(c, 'rgba(0,0,0,0.12)', i, -22, 0.6, 4);
  if (night) {
    ellipse(c, 'rgba(255,230,170,0.22)', 0, -30, 60, 24);
    ellipse(c, 'rgba(200,220,255,0.18)', 0, -60, 90, 20);
  }
  // Ground-floor signs: ticket hall, exits.
  block(c, -70, -30, 20, 6, '#1f3a6e', '#2f5a9e');
  txt(c, '售票處', -60, -27, 3.6, '#ffffff', 900);
  block(c, 50, -30, 22, 6, '#1f3a6e', '#2f5a9e');
  txt(c, '出口 EXIT', 61, -27, 3.4, '#ffffff', 900);
}

// ---------------------------------------------------------------------------
// Chihkan Tower: two pavilions over the old Fort Provintia foundations
// ---------------------------------------------------------------------------

function pavilion(c: Ctx, cx: number, by: number, w: number, night: boolean, name: string): void {
  // Two storeys with balconies, grey-green tiled roofs curling into swallow tails.
  const h1 = 30, h2 = 26;
  // Ground floor.
  block(c, cx - w / 2, by - h1, w, h1, '#efe7d6', '#f8f3e6', '#b9ad96');
  for (const k of [-0.4, -0.13, 0.13, 0.4]) column(c, cx + k * w, by - h1 - 2, by);
  doors(c, cx - 7, by - 18, 14, 18, night);
  for (const s of [-1, 1]) shutter(c, cx + s * w * 0.27 - 4, by - 22, 8, 10, night);
  // Balcony and lower roof.
  block(c, cx - w / 2 - 4, by - h1 - 4, w + 8, 4, '#8a5a3a', '#a8785a', '#5a3a22');
  for (let i = cx - w / 2 - 2; i < cx + w / 2 + 2; i += 3.5) rect(c, '#5a3a22', i, by - h1 - 10, 1, 6);
  rect(c, '#8a5a3a', cx - w / 2 - 4, by - h1 - 10.5, w + 8, 1.2);
  swallowRoof(c, cx - w / 2 - 8, cx + w / 2 + 8, by - h1 - 12, 9, TILE_GREY, '#8a9a8a');
  // Upper floor, set back.
  const w2 = w * 0.78, top2 = by - h1 - 22 - h2;
  block(c, cx - w2 / 2, top2, w2, h2, '#efe7d6', '#f8f3e6', '#b9ad96');
  for (const k of [-0.42, 0, 0.42]) column(c, cx + k * w2, top2 - 2, top2 + h2);
  for (const k of [-0.22, 0.22]) shutter(c, cx + k * w2 - 4, top2 + 6, 8, 11, night, '#7a4a2e');
  rect(c, '#8a5a3a', cx - w2 / 2 - 3, top2 + h2 - 1, w2 + 6, 1.5);
  plaque(c, name, cx, top2 + 2, Math.min(w2 - 6, name.length * 7 + 6), 7.5, '#1f3a2a');
  swallowRoof(c, cx - w2 / 2 - 9, cx + w2 / 2 + 9, top2 - 1, 12, TILE_GREY, '#8a9a8a', true);
  // Lanterns under both eaves.
  for (const k of [-0.36, 0.36]) {
    redLantern(c, cx + k * w, by - h1 - 9, night, 3);
    redLantern(c, cx + k * w2, top2 + 2, night, 2.6);
  }
}

function chihkan(c: Ctx, night: boolean): void {
  shadow(c, 0, 0, 86, 4, 0.3);
  // Red-brick podium (the Dutch fort's base) with a stone balustrade and steps.
  brickwork(c, -86, -16, 172, 16, 5, '#9a4a36', '#b86048');
  block(c, -86, -19, 172, 3.4, '#cfc9bd', '#e4dfd5', '#9a948a');
  for (let i = -82; i < 86; i += 7) block(c, i, -25, 2.2, 6, '#cfc9bd', '#e4dfd5', '#9a948a');
  rect(c, '#cfc9bd', -86, -26, 172, 1.6);
  // Steps up the middle.
  for (let k = 0; k < 4; k++) block(c, -14 + k * 1.5, -4 * (k + 1), 28 - k * 3, 4, '#d8d2c6', '#ece8e0', '#9a948a');
  // Terrace floor.
  rect(c, '#c9bfae', -84, -30, 168, 5);
  // The two pavilions: 海神廟 (left) and 文昌閣 (right), and a low link wall between them.
  block(c, -16, -46, 32, 20, '#efe7d6', '#f8f3e6', '#b9ad96');
  swallowRoof(c, -20, 20, -46, 6, TILE_GREY, '#8a9a8a');
  pavilion(c, -50, -30, 56, night, '海神廟');
  pavilion(c, 50, -30, 56, night, '文昌閣');
  // Creepers and a frangipani by the podium.
  for (let i = 0; i < 7; i++) ellipse(c, i % 2 ? '#3f7a35' : '#5a9a48', -84 + rnd(i, 1) * 30, -14 + rnd(i, 2) * 10, 3, 2);
  if (night) ellipse(c, 'rgba(255,200,120,0.18)', 0, -60, 92, 40);
}

// ---------------------------------------------------------------------------
// Temples: the Confucius Temple's great hall; Matsu / Shuixian halls
// ---------------------------------------------------------------------------

function confucius(c: Ctx, night: boolean): void {
  shadow(c, 0, 0, 88, 4, 0.3);
  // Raised stone platform (丹墀) with a balustrade and a wide stair.
  block(c, -88, -14, 176, 14, '#c9c2b4', '#dcd6ca', '#8f897d');
  for (let i = -84; i < 88; i += 8) block(c, i, -21, 2.4, 7, '#d8d2c6', '#ece8e0', '#9a948a');
  rect(c, '#d8d2c6', -88, -22, 176, 1.8);
  for (let k = 0; k < 4; k++) block(c, -20 + k * 2, -3.5 * (k + 1), 40 - k * 4, 3.5, '#d8d2c6', '#ece8e0', '#9a948a');
  // Hall body: red walls, seven bays of red columns, central doors.
  const by = -22, hw = 72, h = 38;
  block(c, -hw, by - h, hw * 2, h, RED, RED_L, RED_D);
  for (let k = -3; k <= 3; k++) column(c, k * 22, by - h - 2, by, 3.6, '#8a2a1e');
  doors(c, -9, by - 22, 18, 22, night, '#9a2a1e');
  for (const k of [-2, 2]) {
    rect(c, '#2a1810', k * 22 - 6.6, by - 24.6, 13.2, 14.2);
    rect(c, night ? '#ffd88a' : '#1d1a22', k * 22 - 6, by - 24, 12, 13);
    for (let i = -4.5; i <= 4.5; i += 3) rect(c, '#9a2a1e', k * 22 + i - 0.5, by - 24, 1, 13);
    for (let j = -22; j <= -14; j += 3) rect(c, '#9a2a1e', k * 22 - 6, by + j, 12, 0.8);
  }
  plaque(c, '大成殿', 0, by - h + 3, 30, 9, '#1f2a3a');
  // Lower eave roof, then the upper roof: orange-red tiles with swallow tails and ridge cylinders.
  block(c, -hw - 6, by - h - 4, hw * 2 + 12, 4, '#7a2418', '#9a3a2a', '#4a1a10');
  swallowRoof(c, -hw - 12, hw + 12, by - h - 6, 12, TILE_ORANGE, '#e0b060');
  const top2 = by - h - 20;
  block(c, -hw + 14, top2 - 22, hw * 2 - 28, 22, RED, RED_L, RED_D);
  for (let k = -2; k <= 2; k++) column(c, k * 24, top2 - 24, top2, 3, '#8a2a1e');
  for (let k = -2; k <= 2; k++) {
    rect(c, '#2a1810', k * 24 - 5, top2 - 18, 10, 12);
    rect(c, night ? '#ffd88a' : '#2a1810', k * 24 - 4.4, top2 - 17.4, 8.8, 10.8);
    for (let i = -3; i <= 3; i += 3) rect(c, '#9a2a1e', k * 24 + i - 0.4, top2 - 17.4, 0.8, 10.8);
  }
  swallowRoof(c, -hw + 4, hw - 4, top2 - 24, 16, TILE_ORANGE, '#e0b060', true);
  // 通天筒: the two ridge cylinders unique to Tainan's Confucius Temple, and owls on the eaves.
  for (const s of [-1, 1]) {
    block(c, s * 30 - 1.6, top2 - 24 - 16 - 8, 3.2, 8, '#d8a050', '#f0c070', '#a07030');
    ellipse(c, '#f0c070', s * 30, top2 - 48, 1.6, 0.8);
  }
  for (const s of [-1, 1]) {
    const ox = s * (hw - 2);
    ellipse(c, '#3a3a3a', ox, by - h - 8, 1.6, 2);
    ellipse(c, '#ffd34e', ox - 0.5, by - h - 8.6, 0.4, 0.4);
    ellipse(c, '#ffd34e', ox + 0.5, by - h - 8.6, 0.4, 0.4);
  }
  // Lanterns along the lower eave.
  for (const k of [-2.5, -1.5, 1.5, 2.5]) redLantern(c, k * 22, by - h - 2, night, 3);
  if (night) ellipse(c, 'rgba(255,190,120,0.2)', 0, by - 20, 80, 30);
}

/** A richly decorated Taiwanese temple hall. Variant 0 = 開台天后宮 (Matsu), 8+ = 水仙宮 (Shuixian). */
function matsu(c: Ctx, night: boolean, variant: number): void {
  const shui = variant >= 8;
  shadow(c, 0, 0, 64, 3.6, 0.3);
  // Stone platform, red walls, stone dragon columns.
  block(c, -64, -8, 128, 8, '#c9c2b4', '#dcd6ca', '#8f897d');
  for (let k = 0; k < 3; k++) block(c, -14 + k * 2, -2.6 * (k + 1), 28 - k * 4, 2.6, '#d8d2c6', '#ece8e0', '#9a948a');
  const by = -8, h = 34;
  block(c, -58, by - h, 116, h, shui ? '#e8dcc4' : RED, shui ? '#f4ece0' : RED_L, shui ? '#a8987a' : RED_D);
  // Decorative brick dado.
  brickwork(c, -58, by - 10, 116, 10, 9);
  for (const k of [-46, -24, 24, 46]) {
    block(c, k - 2.4, by - h - 2, 4.8, h + 2, '#8a8f8a', '#aab0aa', '#5a5f5a');
    // Dragon coiling round the column.
    for (let j = 0; j < 5; j++) ellipse(c, '#3fa36f', k + Math.sin(j * 1.4) * 2.2, by - h + 4 + j * 6, 1.5, 2);
  }
  doors(c, -9, by - 24, 18, 24, night, shui ? '#9a2a1e' : '#9a2a1e');
  for (const s of [-1, 1]) {
    // Side doors with painted door gods and a round window above.
    doors(c, s * 36 - 6, by - 18, 12, 18, night, '#7a3a2e');
    ellipse(c, '#2a1810', s * 36, by - 25, 4.2, 4.2);
    ellipse(c, night ? '#ffd88a' : '#3a5a4a', s * 36, by - 25, 3.4, 3.4);
    for (let a = 0; a < 6; a++) line(c, '#9a2a1e', 0.6, [s * 36, by - 25, s * 36 + Math.cos(a) * 3.4, by - 25 + Math.sin(a) * 3.4]);
  }
  plaque(c, shui ? '水仙宮' : '開台天后宮', 0, by - h + 2.5, shui ? 28 : 44, 8.5, shui ? '#1f3a2a' : '#1f2a3a');
  // Hanging boards and lanterns.
  for (const k of [-20, 20]) redLantern(c, k, by - h - 1, night, 3.2);
  for (const k of [-50, 50]) redLantern(c, k, by - h - 1, night, 3.2);
  // Lower eave and the main swallow-tail roof with dragons; a 剪黏 mosaic frieze.
  block(c, -62, by - h - 4, 124, 4, '#7a2418', '#9a3a2a', '#4a1a10');
  for (let i = -58; i < 60; i += 4) rect(c, ['#3fa36f', '#ffd34e', '#3a8fd9', '#e2493b'][Math.floor(Math.abs(i / 4)) % 4], i, by - h - 3.4, 3, 2.6);
  swallowRoof(c, -70, 70, by - h - 6, 14, shui ? '#6a7a4a' : TILE_ORANGE, shui ? '#9ab070' : '#e0b060', true);
  const top2 = by - h - 22;
  block(c, -40, top2 - 14, 80, 14, shui ? '#e8dcc4' : RED, shui ? '#f4ece0' : RED_L, shui ? '#a8987a' : RED_D);
  for (let k = -1; k <= 1; k++) rect(c, night ? '#ffd88a' : '#2a1810', k * 22 - 4, top2 - 11, 8, 8);
  swallowRoof(c, -46, 46, top2 - 15, 13, shui ? '#6a7a4a' : TILE_ORANGE, shui ? '#9ab070' : '#e0b060', true);
  if (night) ellipse(c, 'rgba(255,160,90,0.22)', 0, by - 16, 60, 26);
}

/** The 全臺首學 gate (variant 0) and Fort Zeelandia's whitewashed gate (variant 1). */
function gate(c: Ctx, night: boolean, variant: number): void {
  if (variant === 1) {
    shadow(c, 0, 0, 34, 3, 0.3);
    block(c, -32, -34, 64, 34, '#efe8dc', '#f9f5ee', '#b9b0a0');
    for (let i = 0; i < 10; i++) ellipse(c, 'rgba(120,80,60,0.18)', -28 + rnd(i, 1) * 56, -30 + rnd(i, 2) * 26, 2.5, 1.6);
    // Arch opening.
    c.fillStyle = '#3a2a20';
    c.beginPath();
    c.moveTo(-12, 0);
    c.lineTo(-12, -16);
    c.arc(0, -16, 12, Math.PI, 0);
    c.lineTo(12, 0);
    c.closePath();
    c.fill();
    brickwork(c, -32, -38, 64, 4, 3, '#9c4a33', '#b85f44');
    for (let i = -30; i < 32; i += 6) block(c, i, -43, 4, 5, '#efe8dc', '#f9f5ee', '#b9b0a0');
    plaque(c, '安平古堡', 0, -32, 30, 7.5, '#3a4a5a');
    txt(c, 'ZEELANDIA 1624', 0, -23.6, 2.6, '#6a5a4a', 800);
    return;
  }
  shadow(c, 0, 0, 34, 3, 0.3);
  // Four red posts carrying a roofed beam; the plaque hangs in the middle.
  for (const x of [-26, -10, 10, 26]) column(c, x, -48, 0, 3.6);
  block(c, -32, -50, 64, 5, RED, RED_L, RED_D);
  block(c, -22, -62, 44, 12, RED, RED_L, RED_D);
  plaque(c, '全臺首學', 0, -60, 36, 9, '#1f2a3a');
  swallowRoof(c, -36, 36, -64, 9, TILE_ORANGE, '#e0b060', true);
  swallowRoof(c, -34, -14, -50, 6, TILE_ORANGE, '#e0b060');
  swallowRoof(c, 14, 34, -50, 6, TILE_ORANGE, '#e0b060');
  redLantern(c, -18, -46, night, 3);
  redLantern(c, 18, -46, night, 3);
}

// ---------------------------------------------------------------------------
// Anping: Fort Zeelandia's watchtower, old walls, the Tree House
// ---------------------------------------------------------------------------

function fortTower(c: Ctx, night: boolean): void {
  shadow(c, 0, 0, 40, 3.6, 0.3);
  // Whitewashed terrace with a parapet and a red-brick stair.
  block(c, -40, -26, 80, 26, '#f1ebe0', '#faf7f1', '#b9b0a0');
  for (let i = 0; i < 12; i++) ellipse(c, 'rgba(120,80,60,0.15)', -36 + rnd(i, 3) * 72, -22 + rnd(i, 4) * 18, 2.6, 1.6);
  brickwork(c, -40, -29, 80, 3.5, 4, '#9c4a33', '#b85f44');
  for (let i = -38; i < 40; i += 6) block(c, i, -34, 4, 5, '#f1ebe0', '#faf7f1', '#b9b0a0');
  for (let k = 0; k < 3; k++) block(c, -8 + k, -3 * (k + 1), 16 - k * 2, 3, '#b85f44', '#c8785c', '#7e3322');
  // The square white tower with a balcony, slit windows and a red observation cap.
  block(c, -16, -96, 32, 66, '#f4efe4', '#fcf9f3', '#bdb4a4');
  rect(c, 'rgba(120,80,60,0.12)', -16, -96, 32, 66);
  for (let j = 0; j < 4; j++) rect(c, night ? '#ffd88a' : '#2a3448', -1.5, -90 + j * 15, 3, 7);
  block(c, -20, -62, 40, 3, '#e8e2d4', '#f4f0e8', '#a89f90');
  for (let i = -19; i < 20; i += 4) rect(c, '#a89f90', i, -66, 1, 4);
  block(c, -18, -104, 36, 8, '#f4efe4', '#fcf9f3', '#bdb4a4');
  for (let i = -17; i < 18; i += 5) rect(c, night ? '#ffe6a8' : '#5d86a4', i, -103, 3.5, 6);
  poly(c, '#a8453a', [-20, -104, 20, -104, 0, -118]);
  poly(c, '#c45a48', [-18, -104, 0, -104, 0, -117]);
  rect(c, '#8a9096', -0.5, -126, 1, 8);
  // Dutch flag fluttering at the mast.
  for (let k = 0; k < 3; k++) rect(c, ['#ae1c28', '#ffffff', '#21468b'][k], 0.5, -126 + k * 1.8, 8, 1.8);
  if (night) ellipse(c, 'rgba(255,240,200,0.3)', 0, -100, 26, 10);
  // Old cannon balls and a plaque on the terrace.
  for (let i = 0; i < 4; i++) ellipse(c, '#3a3e44', 24 + (i % 2) * 3.6, -5 - Math.floor(i / 2) * 3, 1.8, 1.8);
  plaque(c, '熱蘭遮城', -24, -22, 24, 6, '#3a4a5a');
}

function fortWall(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, v = d.variant ?? 0;
  art(c, d, 'tnFortWall', w + 12, 44, (cc) => {
    const L = -w / 2;
    shadow(cc, 0, 0, w / 2 + 2, 2.6, 0.3);
    // Uneven top edge.
    const hs = Array.from({ length: Math.ceil(w / 6) + 1 }, (_, i) => 14 + rnd(i + v * 11, 1) * (v === 2 ? 12 : 6));
    for (let i = 0; i < hs.length - 1; i++) {
      const x = L + i * 6, hh = hs[i];
      brickwork(cc, x, -hh, 6.4, hh, i + v * 7, '#9c4a33', '#b85f44');
      rect(cc, 'rgba(232,220,196,0.5)', x, -hh, 6.4, 1.2);
    }
    // Lime crust and moss.
    for (let i = 0; i < w / 5; i++) ellipse(cc, i % 3 ? 'rgba(232,220,196,0.45)' : 'rgba(110,138,74,0.6)', L + rnd(i, 2) * w, -4 - rnd(i, 3) * 12, 2.6, 1.6);
    if (v === 0) {
      // Banyan roots creeping down the face.
      for (let i = 0; i < 5; i++) {
        const rx = L + 6 + rnd(i, 4) * (w - 12);
        line(cc, '#6a5038', 1.6, [rx, -22, rx + 2, -14, rx - 1.5, -6, rx + 1, 0]);
        line(cc, '#8a7050', 0.6, [rx + 0.4, -20, rx + 2.2, -14]);
      }
    } else if (v === 1) plaque(cc, '普羅民遮城', 0, -12, 34, 6.5, '#3a4a5a');
  });
}

function treeHouse(c: Ctx, night: boolean): void {
  shadow(c, 0, 0, 72, 4, 0.3);
  // Roofless brick warehouse walls (Tait & Co.), arched doorways, a steel walkway.
  brickwork(c, -70, -44, 140, 44, 21, '#8e4a3a', '#a85e4a');
  for (const x of [-48, 0, 48]) {
    c.fillStyle = night ? '#2a2420' : '#3a3028';
    c.beginPath();
    c.moveTo(x - 8, 0);
    c.lineTo(x - 8, -20);
    c.arc(x, -20, 8, Math.PI, 0);
    c.lineTo(x + 8, 0);
    c.closePath();
    c.fill();
  }
  for (const x of [-24, 24]) {
    rect(c, '#2a1810', x - 5, -36, 10, 10);
    rect(c, night ? '#1d1a22' : '#5a7a6a', x - 4.4, -35.4, 8.8, 8.8);
  }
  rect(c, 'rgba(232,220,196,0.4)', -70, -44, 140, 1.4);
  // Steel walkway on the left wall.
  block(c, -72, -47, 60, 2, '#6a7076', '#8a9096', '#4a5056');
  for (let i = -70; i < -12; i += 4) rect(c, '#6a7076', i, -52, 0.8, 5);
  rect(c, '#6a7076', -72, -52.5, 60, 0.8);
  // The banyan: trunks fused with the walls, a huge spreading canopy, curtains of aerial roots.
  const trunk = '#5a4632', root = '#7a6448', rootL = '#9a8468';
  for (const [tx, tw] of [[-30, 14], [20, 18], [56, 10]] as const) {
    poly(c, trunk, [tx - tw / 2, 0, tx - tw / 2 + 2, -60, tx + tw / 2 - 2, -60, tx + tw / 2, 0]);
    for (let k = 0; k < 4; k++) rect(c, 'rgba(255,255,255,0.08)', tx - tw / 2 + 2 + k * 3, -56, 0.8, 50);
  }
  // Roots over the brick.
  for (let i = 0; i < 18; i++) {
    const rx = -66 + rnd(i, 5) * 130, top = -44 - rnd(i, 6) * 10, len = 20 + rnd(i, 7) * 26;
    line(c, i % 3 ? root : rootL, 1.2 + rnd(i, 8) * 1.2, [rx, top, rx + (rnd(i, 9) - 0.5) * 6, top + len * 0.5, rx + (rnd(i, 10) - 0.5) * 8, top + len]);
  }
  // Hanging aerial roots.
  for (let i = 0; i < 26; i++) {
    const rx = -72 + rnd(i, 11) * 144, top = -70 - rnd(i, 12) * 14, len = 10 + rnd(i, 13) * 30;
    line(c, 'rgba(90,70,50,0.8)', 0.6, [rx, top, rx + (rnd(i, 14) - 0.5) * 3, top + len]);
  }
  // Canopy: layered clusters.
  const dark = '#2a5a34', mid = '#3f8a48', light = '#6ab860';
  for (const [dx, dy, r] of [[-50, -78, 24], [-14, -86, 26], [24, -84, 26], [56, -76, 22], [-30, -96, 18], [10, -100, 20], [40, -96, 18], [0, -70, 22]] as const) ellipse(c, dark, dx, dy, r, r * 0.72);
  for (const [dx, dy, r] of [[-44, -82, 17], [-10, -92, 19], [26, -90, 19], [52, -80, 15], [-26, -100, 12], [12, -104, 14], [40, -100, 12]] as const) ellipse(c, mid, dx - 2, dy - 2, r, r * 0.72);
  for (const [dx, dy, r] of [[-40, -88, 7], [-8, -98, 8], [28, -96, 8], [50, -86, 6], [10, -108, 6], [-24, -104, 5]] as const) ellipse(c, light, dx - 2, dy - 2, r, r * 0.7);
  for (let i = 0; i < 24; i++) ellipse(c, 'rgba(0,0,0,0.12)', -60 + rnd(i, 15) * 120, -70 - rnd(i, 16) * 34, 1.6, 1.1);
  if (night) ellipse(c, 'rgba(120,220,150,0.16)', 0, -40, 60, 22); // the night light show on the roots
}

// ---------------------------------------------------------------------------
// Shophouse rows
// ---------------------------------------------------------------------------

const SHOP_SIGNS: [string, string, string][][] = [
  // 0 Shennong: craft shops, bars, a tea house.
  [['神農街', '#1f2a3a', '#ffd34e'], ['藝廊', '#2f8a7a', '#ffffff'], ['茶屋', '#8a5a3a', '#fff3b0'], ['老屋咖啡', '#3a2a20', '#f8e0b0'], ['手作燈籠', '#c0392b', '#fff3b0'], ['小酒館', '#1b1d24', '#ff9f43']],
  // 1 Anping old street.
  [['蝦餅', '#e2493b', '#fff3b0'], ['蜜餞', '#ff9f43', '#1b1d24'], ['安平豆花', '#f4f1ea', '#c0392b'], ['劍獅', '#1f3a6e', '#ffd34e'], ['蚵捲', '#2a8a5a', '#ffffff'], ['鹹粿', '#8a2a1e', '#ffd34e']],
  // 2 Newer tile-faced shophouses.
  [['機車行', '#1f3a6e', '#ffffff'], ['藥局', '#2a8a5a', '#ffffff'], ['早餐店', '#ffd34e', '#8a2a1e'], ['洗衣', '#3a8fd9', '#ffffff'], ['五金', '#8a5a3a', '#ffd34e'], ['美髮', '#1b1d24', '#ff6aa0']],
  // 3 Japanese-era Baroque + a convenience store.
  [['林百貨', '#1f2a3a', '#ffd34e'], ['全家便利', '#2a8a5a', '#ffffff'], ['書局', '#5a2a8a', '#ffd34e'], ['冰果室', '#7ad0e0', '#1b1d24'], ['銀樓', '#8a2a1e', '#ffd34e'], ['眼鏡', '#2a5a8a', '#ffffff']],
  // 4 Guohua Street market.
  [['牛肉湯', '#c0392b', '#fff3b0'], ['擔仔麵', '#ffd34e', '#8a2a1e'], ['棺材板', '#8a5a3a', '#fff3b0'], ['碗粿', '#2a8a5a', '#ffffff'], ['春捲', '#ff9f43', '#1b1d24'], ['米糕', '#e2493b', '#ffffff']],
];
const FACADES = [['#e8dcc4', '#f4ece0'], ['#d8c8a8', '#e8dcc4'], ['#c8d4d8', '#dce6e8'], ['#e8e4da', '#f4f1ea'], ['#d8d0c0', '#e8e2d4']];

function shophouse(c: Ctx, d: Decor): void {
  const w = d.w ?? 128, v = (d.variant ?? 0) % 5;
  const units = Math.max(1, Math.round(w / 32)), uw = w / units;
  const seed = Math.round(d.x / 16) * 13 + Math.round(d.y / 16) * 7 + v * 101;
  const old = v <= 1, baroque = v === 3, market = v === 4;
  const H = old ? 72 : 92;
  art(c, d, `tnShops:${seed}`, w + 16, H + 8, (cc, night) => {
    const L = -w / 2;
    shadow(cc, 0, 0, w / 2, 3, 0.3);
    const signs = SHOP_SIGNS[v];
    for (let u = 0; u < units; u++) {
      const x = L + u * uw, s = seed + u * 31;
      const [fa, fl] = FACADES[Math.floor(rnd(s, 1) * FACADES.length)];
      const floors = old ? 2 : rnd(s, 2) < 0.4 ? 3 : 2;
      const top = old ? -52 - Math.floor(rnd(s, 3) * 6) : floors === 3 ? -80 : -62;
      if (old) {
        // Red-brick house with a tiled roof, wooden shutters and a wooden door.
        brickwork(cc, x, top, uw, -top, s, rnd(s, 4) < 0.3 ? '#9a4a36' : BRICK, BRICK_L);
        rect(cc, 'rgba(0,0,0,0.12)', x + uw - 0.8, top, 0.8, -top);
        // Upper window with shutters, a tiny balcony on some.
        shutter(cc, x + uw / 2 - 5, top + 8, 10, 11, night);
        if (rnd(s, 5) < 0.4) {
          block(cc, x + 3, top + 20, uw - 6, 2, WOOD, WOOD_L, '#3a2a1a');
          for (let i = x + 4; i < x + uw - 4; i += 3) rect(cc, '#3a2a1a', i, top + 15, 0.8, 5);
        }
        // Ground floor: door and a shop window or stall counter.
        doors(cc, x + 3, -22, 10, 22, night, rnd(s, 6) < 0.5 ? '#7a4a2e' : '#9a2a1e');
        rect(cc, '#2a1810', x + 15.5, -22.5, uw - 18, 13);
        pane(cc, x + 16, -22, uw - 19, 12, night, s, '#7a4a2e');
        // Horizontal shop board over the door.
        const [t, bg, fg] = signs[(u + Math.floor(rnd(s, 7) * 3)) % signs.length];
        block(cc, x + 2, -31, uw - 4, 8, bg, shade(bg, 0.2), shade(bg, -0.3));
        txt(cc, t, x + uw / 2, -27, Math.min(5.6, (uw - 8) / t.length), fg, 900, 'rgba(0,0,0,0.3)');
        // Roof: red-orange tiles with a little ridge, some sprouting weeds.
        block(cc, x - 2, top - 6, uw + 4, 6, '#b8603a', '#d07a50', '#7e3322');
        for (let i = x - 1; i < x + uw + 2; i += 2.4) rect(cc, 'rgba(60,20,10,0.25)', i, top - 6, 0.6, 6);
        rect(cc, '#7e3322', x - 2, top - 7.2, uw + 4, 1.4);
        if (rnd(s, 8) < 0.4) for (let k = 0; k < 3; k++) ellipse(cc, '#5a8a3a', x + 4 + rnd(s, 9 + k) * (uw - 8), top - 7, 1.6, 1.4);
        if (rnd(s, 10) < 0.5) {
          // Lion-head tile (劍獅) over the door — the Anping charm.
          ellipse(cc, '#c8a050', x + 8, top + 26, 2.6, 2.6);
          ellipse(cc, '#8a2a1e', x + 8, top + 26.6, 1.6, 1.1);
          rect(cc, '#3a3a3a', x + 6, top + 25, 4, 0.7);
        }
      } else {
        // Façade in tiles or plaster; arcaded ground floor (騎樓).
        block(cc, x, top, uw, -top, fa, fl, shade(fa, -0.3));
        if (v === 2) for (let j = top + 2; j < -26; j += 3) rect(cc, 'rgba(0,0,0,0.06)', x, j, uw, 0.6);
        for (let f = 1; f < floors; f++) {
          const wy = -26 - f * 18 - 6;
          if (baroque) {
            // Arched windows with a keystone.
            rect(cc, '#8a8070', x + uw / 2 - 5.5, wy - 2.5, 11, 14);
            pane(cc, x + uw / 2 - 5, wy - 2, 10, 13, night, s + f, '#9a9080');
            cc.fillStyle = fa;
            cc.beginPath();
            cc.arc(x + uw / 2, wy - 2, 5.5, Math.PI, 0);
            cc.lineTo(x + uw / 2 + 5.5, wy - 4);
            cc.lineTo(x + uw / 2 - 5.5, wy - 4);
            cc.closePath();
            cc.fill();
            rect(cc, '#8a8070', x + uw / 2 - 0.8, wy - 8, 1.6, 3);
          } else {
            pane(cc, x + 3, wy, uw - 6, 11, night, s + f);
            if (rnd(s, 20 + f) < 0.55) {
              // Window cage, air-con unit or a balcony with pots.
              for (let i = x + 4; i < x + uw - 4; i += 2.6) rect(cc, '#6a6e74', i, wy, 0.6, 11);
              rect(cc, '#6a6e74', x + 3, wy + 11, uw - 6, 0.8);
            }
            if (rnd(s, 30 + f) < 0.5) block(cc, x + uw - 9, wy + 4, 6, 4.5, '#d8d6d0', '#e8e6e0', '#a8a6a0');
          }
        }
        // Parapet: Baroque pediment or a plain rail with a water tank.
        if (baroque) {
          block(cc, x - 1, top - 4, uw + 2, 4, fl, shade(fl, 0.1), shade(fa, -0.3));
          poly(cc, fl, [x + uw / 2 - 9, top - 4, x + uw / 2, top - 11, x + uw / 2 + 9, top - 4]);
          ellipse(cc, '#8a8070', x + uw / 2, top - 6.5, 1.6, 1.6);
          for (let i = x + 1; i < x + uw - 1; i += 4) block(cc, i, top - 7, 2.2, 3, fl, undefined, shade(fa, -0.3));
        } else {
          block(cc, x - 1, top - 3, uw + 2, 3, fa, fl, shade(fa, -0.3));
          if (rnd(s, 11) < 0.5) {
            block(cc, x + uw - 11, top - 12, 8, 9, '#c8cdd2', '#eef2f4', '#8a9096');
            ellipse(cc, '#e8ecef', x + uw - 7, top - 12, 4, 1.4);
            rect(cc, '#55606a', x + uw - 10, top - 3, 1, 2);
            rect(cc, '#55606a', x + uw - 4, top - 3, 1, 2);
          } else if (rnd(s, 12) < 0.5) {
            // Tin add-on.
            block(cc, x + 1, top - 8, uw - 2, 8, '#8a9aa0', '#a8bcc4', '#5a6a70');
            for (let i = x + 2; i < x + uw - 2; i += 2.2) rect(cc, 'rgba(0,0,0,0.14)', i, top - 8, 0.7, 8);
          }
        }
        // Arcade: pillars and a dark walkway with the shop behind.
        rect(cc, night ? '#2a2420' : '#3a3230', x, -26, uw, 26);
        const [t, bg, fg] = signs[(u + Math.floor(rnd(s, 13) * 2)) % signs.length];
        const conv = baroque && t === '全家便利';
        // Shop front inside the arcade.
        if (conv) {
          block(cc, x + 2, -24, uw - 4, 24, '#f4f1ea', '#ffffff', '#b8b4ac');
          rect(cc, '#2a8a5a', x + 2, -24, uw - 4, 2.4);
          rect(cc, '#3a8fd9', x + 2, -21.6, uw - 4, 1.4);
          pane(cc, x + 4, -18, uw - 8, 12, night, s + 50, '#8a9096');
          if (night) rect(cc, '#ffffff', x + 4, -18, uw - 8, 12);
          for (let k = 0; k < 3; k++) rect(cc, ['#e2493b', '#ffd34e', '#3a8fd9'][k], x + 6 + k * 4, -14 + k, 2.4, 3);
        } else {
          pane(cc, x + 3, -22, uw - 6, 14, night, s + 40, '#5a5048');
          if (market) {
            // Roll-up shutters half down, hanging goods.
            block(cc, x + 2, -24, uw - 4, 6 + Math.floor(rnd(s, 14) * 6), '#9a9ea4', '#b8bcc2', '#6a6e74');
            for (let i = x + 3; i < x + uw - 3; i += 2) rect(cc, 'rgba(0,0,0,0.12)', i, -24, 0.6, 6);
          }
        }
        doors(cc, x + uw / 2 - 4, -8, 8, 8, night, '#5a5048');
        for (const px of [x, x + uw]) block(cc, px - 2, -26, 4, 26, shade(fa, -0.1), fl, shade(fa, -0.35));
        block(cc, x - 2, -30, uw + 4, 4, shade(fa, -0.05), fl, shade(fa, -0.3));
        // Sign board over the arcade and an awning.
        block(cc, x + 1, -38, uw - 2, 8, bg, shade(bg, 0.2), shade(bg, -0.3));
        txt(cc, t, x + uw / 2, -34, Math.min(5.6, (uw - 6) / t.length), fg, 900, 'rgba(0,0,0,0.3)');
        if (night) {
          rect(cc, 'rgba(255,255,255,0.1)', x + 1, -38, uw - 2, 8);
          rect(cc, 'rgba(255,230,170,0.25)', x, -26, uw, 26);
        }
        if (rnd(s, 15) < 0.5) {
          const aw = ['#e2493b', '#2a8a5a', '#ffd34e', '#3a8fd9'][Math.floor(rnd(s, 16) * 4)];
          for (let i = 0; i < uw - 4; i += 4) rect(cc, (i / 4) % 2 ? '#f4f1ea' : aw, x + 2 + i, -42, Math.min(4, uw - 4 - i), 3.5);
          poly(cc, shade(aw, -0.2), [x + 2, -38.5, x + uw - 2, -38.5, x + uw - 4, -36, x + 4, -36]);
        }
      }
    }
    // Cables sagging along the whole row and a couple of pigeons on the roof.
    for (let k = 0; k < 2; k++) line(cc, 'rgba(30,26,22,0.7)', 0.5, [L - 8, -H + 20 + k * 3, 0, -H + 26 + k * 3, w / 2 + 8, -H + 19 + k * 3]);
  });
}

// ---------------------------------------------------------------------------
// Small props
// ---------------------------------------------------------------------------

function busStop(c: Ctx, d: Decor, night: boolean): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 3, 1.2, 0.3, 30);
  rect(c, '#55606a', x - 0.8, y - 34, 1.6, 34);
  block(c, x - 9, y - 46, 18, 13, '#1f3a6e', '#2f5a9e', '#132544');
  txt(c, '高鐵快捷', x, y - 42, 3.6, '#ffffff', 900);
  txt(c, 'SHUTTLE 市區', x, y - 36.6, 2.6, '#ffd34e', 800);
  block(c, x - 7, y - 32, 14, 7, '#f4f1ea', '#ffffff', '#b8b4ac');
  for (let k = 0; k < 3; k++) rect(c, k ? '#8a9096' : '#e2493b', x - 5.5, y - 30.5 + k * 2, 11 - k * 2, 0.8);
  if (night) ellipse(c, 'rgba(255,200,120,0.25)', x, y - 40, 12, 9);
}

function turtle(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  shadow(c, x, y, 10, 2.8, 0.3);
  // Bixi: a stone tortoise with a tall stele on its back (Qing imperial stelae, 1788).
  block(c, x - 9, y - 7, 18, 7, '#8f9aa3', '#b0b8be', '#5a636a');
  ellipse(c, '#7f8a92', x, y - 7, 8.4, 4);
  for (let i = -6; i <= 6; i += 2.4) rect(c, 'rgba(0,0,0,0.2)', x + i, y - 10.4, 0.7, 5);
  for (let i = -5; i <= 5; i += 5) rect(c, 'rgba(0,0,0,0.14)', x - 8, y - 8.6 + (i + 5) * 0.35, 16, 0.5);
  ellipse(c, '#9aa3aa', x - 10, y - 6, 3, 2);
  ellipse(c, '#2a2e33', x - 11, y - 6.8, 0.5, 0.5);
  ellipse(c, '#9aa3aa', x + 9.4, y - 5.4, 1.6, 1.2);
  const sh = 28 + (v % 2) * 3;
  block(c, x - 3.4, y - sh - 7, 6.8, sh, '#a9b2b8', '#c8cfd4', '#6f7a82');
  ellipse(c, '#a9b2b8', x, y - sh - 7, 3.4, 2);
  // A dragon-coil cap and columns of carved characters.
  ellipse(c, '#8f9aa3', x, y - sh - 7.5, 2.6, 1.4);
  for (let j = 0; j < 7; j++) rect(c, 'rgba(40,40,50,0.5)', x - 0.8, y - sh - 2 + j * 3.4, 1.6, 2);
  rect(c, 'rgba(40,40,50,0.25)', x - 2.6, y - sh - 4, 5.2, 0.6);
  if (v === 2) ellipse(c, '#6f8a4a', x + 5, y - 8.4, 2, 1.2);
}

function lion(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, s = (d.variant ?? 0) ? -1 : 1;
  shadow(c, x, y, 6, 2, 0.3);
  block(c, x - 5.5, y - 4, 11, 4, '#8f9aa3', '#b0b8be', '#5a636a');
  ellipse(c, '#9aa3aa', x, y - 10, 4.6, 6);
  ellipse(c, '#a9b2b8', x + s * 1.2, y - 17, 4.4, 4);
  for (let i = 0; i < 6; i++) ellipse(c, '#7f8a92', x + s * 1.2 + Math.cos(i) * 4.2, y - 17 + Math.sin(i) * 3.6, 1.1, 1.1);
  ellipse(c, '#2a2e33', x + s * 2.2, y - 17.6, 0.6, 0.6);
  ellipse(c, '#2a2e33', x + s * 0.2, y - 17.6, 0.6, 0.6);
  rect(c, s > 0 ? '#2a2e33' : '#9aa3aa', x + s * 1.2 - 1.4, y - 15.4, 2.8, 1); // open mouth (male) / closed (female)
  if (s > 0) ellipse(c, '#c0392b', x + 1.2, y - 15, 0.9, 0.5);
  else ellipse(c, '#9aa3aa', x - 4, y - 7, 1.8, 1.4); // the cub under the female's paw
  rect(c, '#c0392b', x - 4, y - 12, 8, 1.2); // red ribbon
}

function incense(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 20;
  shadow(c, x, y, w / 2 + 1, 2.4, 0.3);
  for (const lx of [-w / 3, 0, w / 3]) block(c, x + lx - 1, y - 5, 2, 5, '#5a4a2a', '#7a6a3a');
  block(c, x - w / 2, y - 16, w, 11, '#7a6a3a', '#a89050', '#4a3a1a');
  rect(c, '#c8a850', x - w / 2 + 1, y - 15, w - 2, 0.8);
  for (let i = -w / 2 + 3; i < w / 2 - 2; i += 4) rect(c, 'rgba(0,0,0,0.2)', x + i, y - 13, 1.4, 6);
  block(c, x - w / 2 - 2, y - 19, w + 4, 3, '#7a6a3a', '#a89050', '#4a3a1a');
  for (const s of [-1, 1]) {
    rect(c, '#a89050', x + s * (w / 2 + 2.5) - 0.8, y - 24, 1.6, 7);
    rect(c, '#a89050', x + s * (w / 2 + 1), y - 24, 3, 1.4);
  }
  // Sticks and smoke.
  for (let i = -2; i <= 2; i++) {
    rect(c, '#c0392b', x + i * 2.4 - 0.3, y - 24 - Math.abs(i), 0.6, 5);
    ellipse(c, '#ff8a3a', x + i * 2.4, y - 24.4 - Math.abs(i), 0.5, 0.5);
  }
  steam(c, x, y - 26, t, 4, 0.3);
  steam(c, x + 4, y - 24, t + 1, 3, 0.22);
}

function quayRail(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 128, v = d.variant ?? 0;
  if (v === 1) {
    // Stone balustrade round a pond.
    block(c, x, y - 7, w, 2.2, '#cfc9bd', '#e4dfd5', '#9a948a');
    for (let i = 0; i < w; i += 8) block(c, x + i + 2.6, y - 6, 2.4, 6, '#cfc9bd', '#e4dfd5', '#9a948a');
    block(c, x, y - 1.6, w, 1.6, '#cfc9bd', '#e4dfd5', '#9a948a');
    return;
  }
  // Black iron railing with mooring bollards and (variant 2) a life ring.
  rect(c, '#2a2e33', x, y - 11, w, 1);
  rect(c, '#2a2e33', x, y - 6, w, 0.7);
  for (let i = 2; i < w; i += 6) rect(c, '#2a2e33', x + i, y - 11, 0.8, 11);
  for (let i = 10; i < w; i += 40) {
    block(c, x + i - 2, y - 7, 4, 7, '#3a3e44', '#5a5e64', '#1a1e22');
    ellipse(c, '#5a5e64', x + i, y - 7, 2, 1);
  }
  if (v === 2) {
    const rx = x + w / 2;
    ellipse(c, '#ff6a2a', rx, y - 8, 3.4, 3.4);
    ellipse(c, '#f4f1ea', rx, y - 8, 1.8, 1.8);
    rect(c, '#f4f1ea', rx - 3.4, y - 8.4, 1.6, 0.8);
    rect(c, '#f4f1ea', rx + 1.8, y - 8.4, 1.6, 0.8);
  }
}

function bridgeRail(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, h = d.h ?? 64;
  block(c, x - 1, y - 8, 2.4, h + 8, '#8a9096', '#aab0b6', '#5a6066');
  for (let j = 0; j < h; j += 8) rect(c, '#55606a', x - 1.6, y + j - 8, 3.6, 1);
  rect(c, '#55606a', x - 1.6, y + h - 1, 3.6, 1);
}

function boat(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0, w = d.w ?? 56;
  const s = hash(Math.round(d.x), Math.round(d.y), 741);
  const bob = Math.sin(t * 1.3 + s * 6) * 0.8;
  const x = d.x, y = d.y + bob;
  const night = isNight();
  // Wake ripples.
  ellipse(c, 'rgba(255,255,255,0.22)', x, y + 1, w / 2 + 6, 3);
  ellipse(c, 'rgba(255,255,255,0.14)', x - 4, y + 2.4, w / 2 + 10, 2);
  if (v === 2) {
    // Little wooden rowing boat.
    poly(c, '#7a5a3a', [x - 18, y - 4, x - 14, y + 1, x + 14, y + 1, x + 18, y - 4]);
    poly(c, '#a8784a', [x - 16, y - 4, x - 13, y - 1, x + 13, y - 1, x + 16, y - 4]);
    rect(c, '#5a4632', x - 8, y - 3.6, 1.2, 3); rect(c, '#5a4632', x + 6, y - 3.6, 1.2, 3);
    person(c, x - 1, y - 3, 6, 'left', 0, t, 3);
    line(c, '#6a5038', 0.9, [x + 4, y - 10, x + 14, y - 1]);
    return;
  }
  if (v === 1) {
    // Open sampan: wooden hull, a tyre fender, nets, a fisherman standing.
    poly(c, '#5a4632', [x - w / 2, y - 6, x - w / 2 + 6, y + 1, x + w / 2 - 6, y + 1, x + w / 2 + 2, y - 7]);
    poly(c, '#8a6a44', [x - w / 2 + 2, y - 6, x - w / 2 + 7, y - 2, x + w / 2 - 7, y - 2, x + w / 2, y - 7]);
    rect(c, '#3a8fd9', x - w / 2 + 2, y - 5.2, w - 3, 1.2);
    ellipse(c, '#1b1d24', x - w / 2 + 10, y - 3, 2.2, 2.6);
    ellipse(c, '#2f6fa8', x + 8, y - 7, 6, 2.4); // heaped nets
    ellipse(c, '#4a8ad0', x + 10, y - 8, 3, 1.4);
    block(c, x + w / 2 - 12, y - 14, 8, 8, '#8a9096', '#aab0b6', '#5a6066'); // outboard cabin
    person(c, x - 6, y - 4, 11, 'down', 0, t, 1);
    return;
  }
  // Canal tour boat: white hull with a blue stripe, a canopy on posts, passengers in a row.
  poly(c, '#d8d6d0', [x - w / 2, y - 8, x - w / 2 + 5, y + 1, x + w / 2 - 5, y + 1, x + w / 2 + 3, y - 9]);
  poly(c, '#f4f1ea', [x - w / 2 + 1, y - 8, x - w / 2 + 5, y - 3, x + w / 2 - 5, y - 3, x + w / 2 + 1, y - 9]);
  rect(c, '#2f6fa8', x - w / 2 + 2, y - 6, w - 2, 1.8);
  rect(c, '#1f3a6e', x - w / 2 + 2, y - 1.6, w - 4, 1.2);
  txt(c, '運河遊船', x + w / 2 - 12, y - 4.6, 2.4, '#1f3a6e', 900);
  // Passengers.
  const n = Math.floor(w / 12);
  for (let i = 0; i < n; i++) person(c, x - w / 2 + 9 + i * 11, y - 7 + (i % 2) * 0.6, 3 + i * 5 + Math.floor(s * 9), i === n - 1 ? 'up' : 'down', i % 3 === 1 ? 4 : 0, t, i);
  // Canopy.
  for (const px of [x - w / 2 + 5, x + w / 2 - 6]) rect(c, '#8a9096', px, y - 30, 1, 22);
  block(c, x - w / 2 + 2, y - 33, w - 2, 4, '#2f6fa8', '#4a8ad0', '#1f3a6e');
  for (let i = 0; i < w - 2; i += 5) rect(c, (i / 5) % 2 ? '#f4f1ea' : '#2f6fa8', x - w / 2 + 2 + i, y - 29.5, Math.min(5, w - 2 - i), 1.6);
  if (night) {
    for (let i = 0; i < w - 6; i += 7) ellipse(c, '#ffd34e', x - w / 2 + 5 + i, y - 27, 0.9, 0.9);
    ellipse(c, 'rgba(255,220,150,0.2)', x, y - 14, w / 2, 12);
  }
  // Skipper at the stern.
  person(c, x + w / 2 - 10, y - 10, 14, 'up', 0, t, 7);
}

function glints(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 128;
  for (let i = 0; i < 9; i++) {
    const k = (t * 0.25 + hash(i, 1, 751)) % 1;
    const gx = d.x + hash(i, 2, 752) * w, gy = d.y - 4 - hash(i, 3, 753) * 60;
    const a = Math.max(0, Math.sin(k * Math.PI * 2 + i)) * 0.55;
    if (a < 0.1) continue;
    rect(c, `rgba(255,255,255,${a})`, gx - 2.2, gy - 0.35, 4.4, 0.7);
    rect(c, `rgba(255,255,255,${a * 0.7})`, gx - 0.35, gy - 1.6, 0.7, 3.2);
  }
}

/** Riders on the canal-side path, sliding along `w` px. */
function cyclists(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 200;
  for (let i = 0; i < 3; i++) {
    const speed = 16 + hash(i, 1, 761) * 8;
    const dir = i % 2 ? -1 : 1;
    const u = ((t * speed) / w + hash(i, 2, 762)) % 1;
    const x = d.x + (dir > 0 ? u : 1 - u) * w, y = d.y + i * 7 + 8;
    const col = ['#e2493b', '#3a8fd9', '#ffd34e'][i];
    const spin = (t * 6 + i) % 1;
    for (const wx of [-5, 5]) {
      ellipse(c, '#2a2e33', x + wx, y - 3, 3, 3);
      ellipse(c, '#9aa0a6', x + wx, y - 3, 1.1, 1.1);
      line(c, 'rgba(160,165,170,0.8)', 0.4, [x + wx - Math.cos(spin * 6) * 2.6, y - 3 - Math.sin(spin * 6) * 2.6, x + wx + Math.cos(spin * 6) * 2.6, y - 3 + Math.sin(spin * 6) * 2.6]);
    }
    line(c, col, 1.1, [x - 5, y - 3, x - 2, y - 8.5, x + 3, y - 8.5, x + 5, y - 3]);
    line(c, col, 1.1, [x - 2, y - 8.5, x + 1, y - 3.5]);
    rect(c, '#2a2e33', x - 3.2, y - 9.6, 3, 1);
    rect(c, '#2a2e33', x + 2.2, y - 10.4, 3, 1);
    person(c, x - 0.5, y - 7, 8 + i * 7, dir > 0 ? 'right' : 'left', 0, t, i);
  }
}

function banyan(c: Ctx, d: Decor): void {
  const v = d.variant ?? 0;
  art(c, d, 'tnBanyan', 104, 86, (cc) => {
    shadow(cc, 0, 0, 28, 5, 0.32, 40);
    const trunk = '#5a4632';
    poly(cc, trunk, [-7, 0, -5, -30, 5, -30, 8, 0]);
    for (const [dx, h] of [[-12, 20], [12, 16], [-18, 12], [20, 10]] as const) line(cc, trunk, 1.8, [dx * 0.3, -32, dx, -32 + h]);
    for (let k = 0; k < 5; k++) rect(cc, 'rgba(255,255,255,0.1)', -4 + k * 2.2, -28, 0.8, 24);
    // Aerial roots.
    for (let i = 0; i < 14; i++) {
      const rx = -40 + rnd(i + v * 5, 1) * 80, top = -44 - rnd(i, 2) * 10, len = 10 + rnd(i + v, 3) * 26;
      line(cc, 'rgba(90,70,50,0.85)', 0.6 + rnd(i, 4) * 0.6, [rx, top, rx + (rnd(i, 5) - 0.5) * 3, top + len]);
    }
    const dark = v === 1 ? '#2a5a34' : '#2f6a3a', mid = v === 1 ? '#3f8a48' : '#4a9a52', light = v === 1 ? '#6ab860' : '#7ac468';
    for (const [dx, dy, r] of [[-30, -52, 18], [0, -62, 22], [30, -52, 18], [-14, -46, 16], [16, -46, 16], [0, -42, 18], [-40, -58, 12], [40, -58, 12]] as const) ellipse(cc, dark, dx, dy, r, r * 0.74);
    for (const [dx, dy, r] of [[-26, -56, 13], [2, -68, 15], [28, -56, 13], [-12, -50, 10], [14, -50, 10], [-36, -62, 8], [36, -62, 8]] as const) ellipse(cc, mid, dx - 1.5, dy - 1.5, r, r * 0.74);
    for (const [dx, dy, r] of [[-24, -62, 5.5], [4, -74, 6.5], [26, -62, 5.5], [-10, -56, 4], [16, -56, 4]] as const) ellipse(cc, light, dx - 1.5, dy - 1.5, r, r * 0.7);
    for (let i = 0; i < 18; i++) ellipse(cc, 'rgba(0,0,0,0.12)', -40 + rnd(i, 6) * 80, -46 - rnd(i, 7) * 28, 1.5, 1);
    if (v === 2) {
      // Red ribbons and a little shrine at the foot (a venerated old tree).
      for (let k = 0; k < 4; k++) rect(cc, '#e2493b', -6 + k * 3.5, -26 + (k % 2) * 2, 1, 8);
      block(cc, 10, -8, 8, 8, '#c0392b', '#e05a48', '#8a2a1e');
      rect(cc, GOLD, 11, -7, 6, 0.6);
    }
  });
}

const STREET_NAMES: [string, string][] = [['神農街', 'Shennong St'], ['延平街', 'Yanping St'], ['國華街', 'Guohua St'], ['府前路', 'Fuqian Rd']];
function streetSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, [zh, en] = STREET_NAMES[(d.variant ?? 0) % STREET_NAMES.length];
  shadow(c, x, y, 2.4, 1, 0.3, 20);
  rect(c, '#55606a', x - 0.7, y - 24, 1.4, 24);
  block(c, x - 12, y - 32, 24, 9, '#2a7a4a', '#3a9a5a', '#1a4a2a');
  txt(c, zh, x, y - 29.4, 4.6, '#ffffff', 900);
  txt(c, en, x, y - 25, 2.4, '#ffffff', 700);
}

function stonePlaque(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 6, 2, 0.3);
  block(c, x - 6, y - 4, 12, 4, '#8f9aa3', '#b0b8be', '#5a636a');
  block(c, x - 4.5, y - 26, 9, 22, '#a9b2b8', '#c8cfd4', '#6f7a82');
  ellipse(c, '#a9b2b8', x, y - 26, 4.5, 2.4);
  for (let j = 0; j < 7; j++) rect(c, 'rgba(40,40,50,0.55)', x - 1, y - 24 + j * 2.8, 2, 1.8);
}

function squirrel(c: Ctx, d: Decor, t: number): void {
  const k = (t * 0.4) % 1;
  const x = d.x + Math.sin(t * 0.8) * 6, y = d.y - (k > 0.5 ? 0 : Math.sin(k * Math.PI * 2) * 3);
  const dir = Math.cos(t * 0.8) >= 0 ? 1 : -1;
  ellipse(c, '#7a5030', x, y - 2, 3, 2);
  ellipse(c, '#7a5030', x + dir * 2.6, y - 3.4, 1.6, 1.4);
  ellipse(c, '#2a1810', x + dir * 3.2, y - 3.7, 0.35, 0.35);
  line(c, '#8a6040', 1.6, [x - dir * 2.6, y - 2, x - dir * 5, y - 6, x - dir * 3, y - 8]);
}

function cannon(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, s = (d.variant ?? 0) ? -1 : 1;
  shadow(c, x, y, 8, 2.2, 0.3);
  block(c, x - 6, y - 5, 12, 5, WOOD, WOOD_L, '#3a2a1a');
  for (const wx of [-5, 5]) {
    ellipse(c, '#3a2a1a', x + wx, y - 2, 2.6, 2.6);
    ellipse(c, WOOD_L, x + wx, y - 2, 1.2, 1.2);
  }
  c.save();
  c.translate(x, y - 7);
  c.rotate(-s * 0.25);
  block(c, -9 * (s > 0 ? 1 : 1) - 1, -2.4, 20, 4.8, '#2a2e33', '#4a4e54', '#121418');
  ellipse(c, '#121418', s * 10, 0, 1.6, 2.2);
  rect(c, '#4a4e54', -4, -3.2, 2, 6.4);
  c.restore();
}

function lanternShop(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 40;
  const night = isNight();
  rect(c, '#5a4a3a', x - w / 2, y - 30, w, 1);
  const cols = ['#e2493b', '#ffd34e', '#ff9f43', '#f4f1ea', '#3a8fd9', '#ff6aa0', '#57c278'];
  const n = Math.floor(w / 7);
  for (let i = 0; i < n; i++) {
    const lx = x - w / 2 + 3.5 + i * 7, sw = Math.sin(t * 1.4 + i) * 0.8 + wind.at(lx, y) * 1.5;
    const col = cols[i % cols.length], r = 2.8 + (i % 2) * 0.6;
    rect(c, '#2a1810', lx - 0.3, y - 30, 0.6, 3);
    ellipse(c, night ? shade(col, 0.2) : col, lx + sw, y - 24 + r, r, r * 0.95);
    rect(c, 'rgba(0,0,0,0.2)', lx + sw - r * 0.5 - 0.3, y - 24 + 0.6, 0.6, r * 1.7);
    ellipse(c, 'rgba(255,240,200,0.5)', lx + sw - r * 0.3, y - 24 + r * 0.7, r * 0.3, r * 0.45);
    if (i % 3 === 0) txt(c, ['福', '春', '囍'][(i / 3) % 3], lx + sw, y - 24 + r, 3, '#1b1d24', 900);
    rect(c, GOLD, lx + sw - 0.3, y - 24 + r * 1.9, 0.6, 2.4);
    if (night) ellipse(c, `rgba(255,170,90,0.22)`, lx + sw, y - 24 + r, r * 2.2, r * 2);
  }
}

function crackerRack(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 24;
  shadow(c, x, y, w / 2 + 1, 2.4, 0.3);
  for (const lx of [-w / 2 + 1, w / 2 - 2]) rect(c, '#8a9096', x + lx, y - 26, 1.2, 26);
  for (let j = 0; j < 3; j++) {
    rect(c, '#8a9096', x - w / 2, y - 8 - j * 8, w, 0.8);
    for (let i = 0; i < Math.floor(w / 6); i++) {
      const bx = x - w / 2 + 3 + i * 6, by = y - 8 - j * 8;
      block(c, bx - 2.4, by - 7, 4.8, 7, ['#f6b8a0', '#ffd0a8', '#f8a070'][(i + j) % 3], '#fff0e8', '#d08860');
      rect(c, 'rgba(255,255,255,0.5)', bx - 1.8, by - 6.4, 1, 5.5);
      rect(c, '#e2493b', bx - 2.4, by - 7.4, 4.8, 1.2);
    }
  }
  block(c, x - w / 2 - 2, y - 32, w + 4, 6, '#e2493b', '#ff6a5a', '#a02a1e');
  txt(c, '蝦餅', x, y - 29, 4.2, '#fff3b0', 900, 'rgba(0,0,0,0.3)');
}

function jarRow(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 36;
  shadow(c, x, y, w / 2 + 1, 2.4, 0.3);
  block(c, x - w / 2, y - 9, w, 9, WOOD, WOOD_L, '#3a2a1a');
  rect(c, '#f2e6cf', x - w / 2 + 1, y - 10, w - 2, 1.2);
  const fills = ['#c0392b', '#ff9f43', '#8a5a3a', '#ffd34e', '#3fa36f', '#9b5fc0'];
  for (let j = 0; j < 2; j++) {
    for (let i = 0; i < Math.floor(w / 7); i++) {
      const jx = x - w / 2 + 3.5 + i * 7 + j * 2, jy = y - 10 - j * 9;
      block(c, jx - 2.6, jy - 8, 5.2, 8, 'rgba(220,235,240,0.7)', 'rgba(255,255,255,0.8)', 'rgba(150,170,180,0.8)');
      rect(c, fills[(i + j * 2) % fills.length], jx - 2.1, jy - 6, 4.2, 5.4);
      for (let k = 0; k < 3; k++) ellipse(c, 'rgba(255,255,255,0.35)', jx - 1 + k * 1.1, jy - 4 + (k % 2), 0.7, 0.5);
      block(c, jx - 2.8, jy - 9.6, 5.6, 1.8, '#c0392b', '#e05a48');
    }
  }
  block(c, x - w / 2 - 2, y - 30, w + 4, 6, '#ff9f43', '#ffbf6a', '#c07020');
  txt(c, '蜜餞', x, y - 27, 4.2, '#1b1d24', 900);
}

function boardwalk(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 160;
  for (let i = -w / 2; i < w / 2; i += 4) rect(c, (i / 4) % 2 ? '#8a6a44' : '#9a7a50', x + i, y - 8, 3.6, 8);
  rect(c, 'rgba(0,0,0,0.2)', x - w / 2, y - 8.6, w, 0.8);
  rect(c, 'rgba(0,0,0,0.2)', x - w / 2, y - 0.8, w, 0.8);
}

// ---------------------------------------------------------------------------
// Objects: food stalls, info boards, booths, the toilet, the rental kiosk and the ATM
// ---------------------------------------------------------------------------

/** A standing information board on two posts: a title and a small English line. */
function board(c: Ctx, cx: number, by: number, w: number, title: string, sub: string, bg = '#1f3a2a', fg = '#fff6dc'): void {
  shadow(c, cx, by, w / 2, 2.2);
  for (const px of [cx - w / 2 + 3, cx + w / 2 - 5]) block(c, px, by - 16, 2.2, 16, '#5a4a3a', '#7a6a58');
  block(c, cx - w / 2, by - 30, w, 15, bg, shade(bg, 0.2), shade(bg, -0.3));
  rect(c, 'rgba(255,255,255,0.25)', cx - w / 2 + 1.5, by - 28.6, w - 3, 0.5);
  txt(c, title, cx, by - 24.4, 5.6, fg, 900, 'rgba(0,0,0,0.35)');
  txt(c, sub, cx, by - 18.8, 2.4, shade(fg, -0.15), 700);
}

const STALLS: Record<string, [label: string, canopy: string, goods: (c: Ctx, x: number, y: number, t: number) => void]> = {
  tn_beefSoup: ['牛肉湯', '#c0392b', (c, x, y, t) => {
    for (let i = 0; i < 3; i++) {
      const bx = x - 12 + i * 12;
      ellipse(c, '#f4f1ea', bx, y, 5, 2.4);
      ellipse(c, '#b8602a', bx, y - 0.6, 3.6, 1.6);
      ellipse(c, '#d9825a', bx - 1, y - 1, 1.4, 0.8);
      ellipse(c, '#5aa860', bx + 1.2, y - 1.2, 0.8, 0.5);
      steam(c, bx, y - 3, t + i, 3, 0.3);
    }
    block(c, x + 16, y - 10, 8, 10, '#9aa0a6', '#c8cdd2', '#5a6066'); // the big soup pot
    ellipse(c, '#c8cdd2', x + 20, y - 10, 4, 1.4);
  }],
  tn_danzai: ['擔仔麵', '#ffd34e', (c, x, y, t) => {
    for (let i = 0; i < 3; i++) {
      const bx = x - 12 + i * 12;
      ellipse(c, '#3a5a8a', bx, y, 5, 2.4);
      ellipse(c, '#e8c070', bx, y - 0.6, 3.6, 1.6);
      ellipse(c, '#ff8a5a', bx + 0.4, y - 1.4, 1.6, 0.9); // the shrimp
      ellipse(c, '#5aa860', bx - 1.6, y - 1, 0.9, 0.5);
      steam(c, bx, y - 3, t + i * 1.3, 2, 0.26);
    }
    // The low stool and the bamboo noodle basket.
    ellipse(c, '#c8a860', x + 18, y - 4, 4.4, 3.2);
    ellipse(c, '#e8c880', x + 18, y - 4.6, 3, 2);
  }],
  tn_coffin: ['棺材板', '#8a5a3a', (c, x, y) => {
    for (let i = 0; i < 3; i++) {
      const bx = x - 12 + i * 12;
      block(c, bx - 5, y - 4, 10, 4, '#e8b060', '#f8d090', '#b07830');
      block(c, bx - 4, y - 6.5, 8, 2.5, '#f8d090', '#ffe8b0', '#c89050');
      ellipse(c, '#f4f1ea', bx, y - 4.4, 2.6, 1.1);
      ellipse(c, '#ff9f43', bx - 0.6, y - 4.8, 0.7, 0.5);
      ellipse(c, '#5aa860', bx + 1, y - 4.6, 0.6, 0.4);
    }
    block(c, x + 16, y - 9, 10, 9, '#3a3e44', '#5a5e64', '#1a1e22'); // the fryer
    rect(c, '#e8b060', x + 17, y - 8, 8, 1.6);
  }],
  tn_shrimp: ['蝦餅', '#ff9f43', (c, x, y) => {
    for (let i = 0; i < 4; i++) {
      const bx = x - 15 + i * 10;
      block(c, bx - 3, y - 8, 6, 8, ['#f6b8a0', '#ffd0a8', '#f8a070'][i % 3], '#fff0e8', '#d08860');
      rect(c, 'rgba(255,255,255,0.45)', bx - 2.2, y - 7.2, 1, 6);
      rect(c, '#e2493b', bx - 3, y - 8.5, 6, 1.2);
    }
    for (let k = 0; k < 6; k++) ellipse(c, '#f8b890', x + 14 + (k % 3) * 2.4, y - 2 - Math.floor(k / 3) * 2, 2, 1.1); // crackers piled
  }],
  tn_candied: ['蜜餞', '#9b5fc0', (c, x, y) => {
    const fills = ['#c0392b', '#ff9f43', '#8a5a3a', '#ffd34e', '#3fa36f'];
    for (let i = 0; i < 5; i++) {
      const jx = x - 16 + i * 8;
      block(c, jx - 2.8, y - 8, 5.6, 8, 'rgba(220,235,240,0.7)', 'rgba(255,255,255,0.8)', 'rgba(150,170,180,0.8)');
      rect(c, fills[i], jx - 2.2, y - 6, 4.4, 5.4);
      block(c, jx - 3, y - 9.6, 6, 1.8, '#c0392b', '#e05a48');
    }
  }],
};

function stallObj(id: string): ObjDrawer {
  const [label, canopy, goods] = STALLS[id];
  return (c, r, t) => stall(c, r, t, label, canopy, (cc, rr, tt) => goods(cc, rr.x + rr.w / 2, rr.y - 14, tt));
}

const OBJECTS: Record<string, ObjDrawer> = {
  ...Object.fromEntries(Object.keys(STALLS).map((id) => [id, stallObj(id)])),
  tn_hsr(c, r) {
    // Entrance doors in the station's glass front, with the ticket-gate sign.
    const cx = r.x + r.w / 2, by = r.y + r.h;
    const night = isNight();
    block(c, cx - 24, by - 30, 48, 30, night ? '#ffe2a8' : '#bcdcee', night ? '#fff0c8' : '#d8ecf6', '#6a7078');
    for (const dx of [-16, -8, 0, 8, 16]) rect(c, '#6a7078', cx + dx - 0.5, by - 30, 1, 30);
    rect(c, '#6a7078', cx - 24, by - 14, 48, 0.8);
    block(c, cx - 14, by - 38, 28, 7, '#ff8a2a', '#ffa850', '#c05a10');
    txt(c, '入口 ENTRANCE', cx, by - 34.6, 3.2, '#ffffff', 900);
    rect(c, 'rgba(0,0,0,0.12)', cx - 24, by - 1, 48, 1);
  },
  tn_shuttle(c, r) {
    // Shuttle timetable board next to the stop.
    const cx = r.x + r.w / 2, by = r.y + r.h;
    shadow(c, cx, by, 6, 2);
    rect(c, '#55606a', cx - 0.7, by - 14, 1.4, 14);
    block(c, cx - 8, by - 26, 16, 12, '#f4f1ea', '#ffffff', '#b8b4ac');
    rect(c, '#1f3a6e', cx - 8, by - 26, 16, 2.4);
    txt(c, 'H31 市區', cx, by - 20.6, 3, '#1f3a6e', 900);
    for (let k = 0; k < 3; k++) rect(c, '#8a9096', cx - 6, by - 17.6 + k * 1.3, 12 - k * 2, 0.6);
  },
  tn_chihkan: (c, r) => board(c, r.x + r.w / 2, r.y + r.h, 46, '赤崁樓', 'Chihkan Tower · 1653', '#1f3a2a'),
  tn_turtles: (c, r) => board(c, r.x + r.w / 2 + 20, r.y + r.h, 36, '贔屭碑', 'Nine stone turtles', '#3a4a5a'),
  tn_confucius: (c, r) => board(c, r.x + r.w / 2 + 44, r.y + r.h, 44, '孔廟', 'Confucius Temple · 1665', '#7a2418'),
  tn_zeelandia: (c, r) => board(c, r.x + r.w / 2 + 26, r.y + r.h, 46, '安平古堡', 'Fort Zeelandia · 1624', '#3a4a5a'),
  tn_treehouse: (c, r) => board(c, r.x + r.w / 2, r.y + r.h, 44, '安平樹屋', 'Anping Tree House', '#2a5a34'),
  tn_lanterns(c, r, t) {
    // The lantern maker's work table: paper, brushes, a half-painted lantern.
    const cx = r.x + r.w / 2, by = r.y + r.h;
    const night = isNight();
    shadow(c, cx, by, r.w / 2 + 1, 2.6, 0.3);
    block(c, cx - r.w / 2, by - 10, r.w, 10, WOOD, WOOD_L, '#3a2a1a');
    rect(c, '#f2e6cf', cx - r.w / 2 + 1, by - 11, r.w - 2, 1.2);
    for (let k = 0; k < 3; k++) rect(c, ['#e2493b', '#ffd34e', '#f4f1ea'][k], cx - 18 + k * 4, by - 12.6, 3.2, 1.6); // paper stacks
    for (let k = 0; k < 4; k++) rect(c, '#3a2a1a', cx + 6 + k * 2, by - 18, 0.8, 7); // brushes in a jar
    block(c, cx + 5, by - 14, 10, 3.4, '#5a7a9a', '#7a9aba');
    ellipse(c, '#e2493b', cx - 4, by - 15, 4.4, 4);
    ellipse(c, 'rgba(255,255,255,0.5)', cx - 5.4, by - 16, 1.2, 1.6);
    txt(c, '福', cx - 4, by - 15, 3.4, GOLD, 900);
    // Lanterns hanging over the table from a bamboo pole.
    rect(c, '#8a7a5a', cx - r.w / 2 - 2, by - 36, r.w + 4, 1);
    for (let i = 0; i < 4; i++) {
      const lx = cx - r.w / 2 + 6 + i * 11, sw = Math.sin(t * 1.5 + i * 1.2) * 0.8;
      redLantern(c, lx + sw, by - 35, night, 3.2);
    }
  },
  tn_boatRide(c, r) {
    const cx = r.x + r.w / 2, by = r.y + r.h;
    shadow(c, cx, by, 14, 2.6, 0.3);
    block(c, cx - 12, by - 26, 24, 26, '#2f6fa8', '#4a8ad0', '#1f3a6e');
    block(c, cx - 14, by - 30, 28, 4, '#f4f1ea', '#ffffff', '#b8b4ac');
    rect(c, isNight() ? '#ffe6a8' : '#bcdcee', cx - 8, by - 22, 16, 9);
    rect(c, '#1f3a6e', cx - 8, by - 13, 16, 1);
    txt(c, '運河遊船', cx, by - 7, 4.2, '#ffffff', 900, 'rgba(0,0,0,0.3)');
    txt(c, 'BOAT TOUR', cx, by - 3, 2.4, '#ffd34e', 800);
    person(c, cx, by - 13, 14, 'down', 0, 0, 2);
  },
  rent_tn(c, r) {
    const cx = r.x + r.w / 2, by = r.y + r.h, w = r.w + 8;
    const night = isNight();
    shadow(c, cx, by, w / 2 + 2, 3, 0.3);
    block(c, cx - w / 2, by - 30, w, 30, '#f4f1ea', '#ffffff', '#b8b4ac');
    rect(c, '#2a8a5a', cx - w / 2, by - 30, w, 3);
    pane(c, cx - w / 2 + 3, by - 24, w - 6, 10, night, 3, '#8a9096');
    block(c, cx - w / 2 - 2, by - 38, w + 4, 8, '#2a8a5a', '#3fa36f', '#1a5a3a');
    txt(c, '租車 RENT', cx, by - 34, 4.2, '#ffffff', 900, 'rgba(0,0,0,0.3)');
    txt(c, '🚲 🛴 🛵', cx, by - 9, 5, '#2a2f3d', 400);
    // Bikes and a scooter lined up in front.
    for (let i = 0; i < 2; i++) {
      const bx = cx - 10 + i * 12;
      ellipse(c, '#2a2e33', bx - 3, by - 2.4, 2.6, 2.6);
      ellipse(c, '#2a2e33', bx + 3, by - 2.4, 2.6, 2.6);
      line(c, i ? '#ffb020' : '#3a8fd9', 1.1, [bx - 3, by - 2.4, bx - 0.5, by - 7, bx + 2.5, by - 7, bx + 3, by - 2.4]);
    }
  },
  wc_tn(c, r) {
    const cx = r.x + r.w / 2, by = r.y + r.h, w = r.w + 6;
    shadow(c, cx, by, w / 2 + 1, 2.8, 0.3);
    block(c, cx - w / 2, by - 28, w, 28, '#d8d6d0', '#eceae4', '#9a988f');
    for (let j = by - 26; j < by; j += 3) rect(c, 'rgba(0,0,0,0.06)', cx - w / 2, j, w, 0.6);
    block(c, cx - w / 2 - 1, by - 31, w + 2, 3, '#8a9aa0', '#a8bcc4', '#5a6a70');
    rect(c, '#2a2420', cx - 9, by - 16, 6, 16);
    rect(c, '#2a2420', cx + 3, by - 16, 6, 16);
    block(c, cx - 11, by - 24, 10, 7, '#3a8fd9', '#5aa8ef');
    block(c, cx + 1, by - 24, 10, 7, '#e2493b', '#f06a5a');
    txt(c, '男', cx - 6, by - 20.4, 4, '#ffffff', 900);
    txt(c, '女', cx + 6, by - 20.4, 4, '#ffffff', 900);
    txt(c, '🚻', cx, by - 29, 4, '#ffffff', 400);
  },
  bank_atmTn(c, r) {
    const cx = r.x + r.w / 2, by = r.y + r.h;
    const night = isNight();
    shadow(c, cx, by, 10, 2.4, 0.3);
    block(c, cx - 9, by - 30, 18, 30, '#3a4a5a', '#5a6a7a', '#1a2a3a');
    block(c, cx - 7, by - 27, 14, 9, night ? '#9ad0f0' : '#7ab0d0', '#bfe6ff', '#2a5a7a');
    for (let k = 0; k < 3; k++) rect(c, 'rgba(255,255,255,0.7)', cx - 5, by - 25 + k * 2.2, 10 - k * 3, 0.7);
    block(c, cx - 7, by - 16, 14, 5, '#9aa0a6', '#c8cdd2', '#5a6066');
    for (let j = 0; j < 3; j++) for (let i = 0; i < 4; i++) rect(c, '#2a2e33', cx - 5.5 + i * 3, by - 15 + j * 1.5, 2, 1);
    rect(c, '#1b1d24', cx - 5, by - 9, 10, 1.6);
    block(c, cx - 11, by - 36, 22, 6, '#2a8a5a', '#3fa36f', '#1a5a3a');
    txt(c, 'ATM 提款機', cx, by - 33, 3.4, '#ffffff', 900);
    if (night) ellipse(c, 'rgba(160,220,255,0.25)', cx, by - 22, 12, 10);
  },
};

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

function drawDecor(c: Ctx, d: Decor, t: number): boolean {
  if (!d.kind.startsWith('tn_')) return false;
  const kind = d.kind.slice(3);
  if (drawLife(c, kind, d, t)) return true;
  const night = isNight();
  switch (d.kind) {
    case 'tn_hsrStation':
      landmark(c, d, 'tnHsr', hsrStation);
      return true;
    case 'tn_chihkan':
      landmark(c, d, 'tnChihkan', chihkan);
      return true;
    case 'tn_confucius':
      landmark(c, d, 'tnConfucius', confucius);
      return true;
    case 'tn_matsu':
      landmark(c, d, `tnMatsu${(d.variant ?? 0) >= 8 ? 'S' : 'M'}`, (cc, n) => matsu(cc, n, d.variant ?? 0));
      return true;
    case 'tn_fortTower':
      landmark(c, d, 'tnFort', fortTower);
      return true;
    case 'tn_treeHouse':
      landmark(c, d, 'tnTreeHouse', treeHouse);
      return true;
    case 'tn_gate':
      art(c, d, 'tnGate', 80, 70, (cc, n) => gate(cc, n, d.variant ?? 0));
      return true;
    case 'tn_shophouse':
      shophouse(c, d);
      return true;
    case 'tn_fortWall':
      fortWall(c, d);
      return true;
    case 'tn_banyan':
      banyan(c, d);
      return true;
    case 'tn_busStop':
      busStop(c, d, night);
      return true;
    case 'tn_turtle':
      turtle(c, d);
      return true;
    case 'tn_lion':
      lion(c, d);
      return true;
    case 'tn_incense':
      incense(c, d, t);
      return true;
    case 'tn_quayRail':
      quayRail(c, d);
      return true;
    case 'tn_bridgeRail':
      bridgeRail(c, d);
      return true;
    case 'tn_boat':
      boat(c, d, t);
      return true;
    case 'tn_glints':
      glints(c, d, t);
      return true;
    case 'tn_cyclists':
      cyclists(c, d, t);
      return true;
    case 'tn_streetSign':
      streetSign(c, d);
      return true;
    case 'tn_plaque':
      stonePlaque(c, d);
      return true;
    case 'tn_squirrel':
      squirrel(c, d, t);
      return true;
    case 'tn_cannon':
      cannon(c, d);
      return true;
    case 'tn_lanternShop':
      lanternShop(c, d, t);
      return true;
    case 'tn_crackerRack':
      crackerRack(c, d);
      return true;
    case 'tn_jarRow':
      jarRow(c, d);
      return true;
    case 'tn_boardwalk':
      boardwalk(c, d);
      return true;
    default:
      return false;
  }
}

registerPropDrawers({
  objects: OBJECTS,
  decor: drawDecor,
  animatedObjects: [...Object.keys(STALLS), 'tn_lanterns'],
  animatedDecor: [...LIFE_ANIMATED.map((k) => `tn_${k}`), 'tn_incense', 'tn_boat', 'tn_glints', 'tn_cyclists', 'tn_squirrel', 'tn_lanternShop'],
});
